import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { removeDuplicatedFromCart } from '../../api/cart/remove-duplicated-from-cart';
import Blinder from '../../common/blinder/blinder';
import useChangeFavorite from '../../hooks/use-change-favorite';
import useIsMobile from '../../hooks/use-is-mobile';
import { useWishlistReduxBridge } from '../../hooks/use-wishlist-redux-bridge';
import translation from '../../languages';
import { reset } from '../../reducers/cart';
import qualityEvents from '../../services/quality-events/quality-events';
import classNames from '../../utils/classnames';
import ErrorMessage, { ErrorMessageType } from '../payment/components/error-message/error-message';
import CartHeaderMobile from './cart-header-mobile/cart-header-mobile';
import CartItems from './cart-items/cart-items';
import MaterialCartSummary from './cart-summary/material-cart-summary';
import Favorites from './favorites/favorites';
import NoResults from './no-results/no-results';
import RecommendedPromoMaterials from './recommended-promo-materials/recommended-promo-materials';
import './cart.scss';

type CartProps = TranslationProps;

export type VoucherError = { text: string } | null;

const CartPromoErrorReason: CartPromoErrorReason[] = ['used_voucher', 'used_credits'];

const Cart: FC<CartProps> = ({ t }) => {
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const { changeFavoriteItem } = useChangeFavorite();
    const user = useSelector((state: RootState) => state.main.user);
    const cart = useSelector((state: RootState) => state.main.cart);
    const { cartItems = [], itemsInBundles } = cart;
    const [blinder, showBlinder] = useState(false);
    const [voucherError, setVoucherError] = useState<VoucherError>(null);
    const [paypalExpressError, setPaypalExpressError] = useState<ErrorMessageType | null>(null);
    const [promoError, setPromoError] = useState<string | null>(null);

    // Cart renders no header, so run the wishlist bridge here (normally in FavoritesBtn)
    useWishlistReduxBridge();

    useEffect(() => {
        // Prefetch payment chunk — nearly all users navigate from cart to payment
        import(/* webpackChunkName: "route-payment", webpackPrefetch: true */ '../payment/payment').catch(() => {});
    }, []);

    useEffect(() => {
        if (!user.loaded || !cart.loaded) {
            return;
        }

        qualityEvents.cartPage.viewCart({ cartTotalValue: cart.finalPrice ?? '0', cartItems });
    }, [cart.loaded, user.loaded]);

    useEffect(() => {
        if (cart.promo?.reason && CartPromoErrorReason.includes(cart.promo?.reason)) {
            setPromoError(t('referrals-landing:cartPromo.voucherError'));
        } else {
            setPromoError(null);
        }
    }, [cart?.promo?.applied, cart?.promo?.reason]);

    function removeDuplicated() {
        removeDuplicatedFromCart().then(
            ({
                data: {
                    items: { cart }
                }
            }) => {
                dispatch(reset(cart));
            }
        );
    }

    const onClosePaypalExpressError = () => {
        setPaypalExpressError(null);
    };

    const onCloseVoucherError = () => {
        setVoucherError(null);
    };

    const onClosePromoError = () => {
        setPromoError(null);
    };

    const handleChangeFavorite = (material: Material, add: boolean) => {
        changeFavoriteItem({ item: material, add, worldOverride: material.world });
    };

    const handleReset = (cart: Cart) => {
        dispatch(reset(cart));
    };

    const CartContent = (
        <>
            <div className='cart-content d-flex flex-wrap row'>
                <div className='col-lg-8 col-md-12 col-sm-12 col-xs-12 items-wrapper'>
                    <CartItems cart={cart} changeFavorite={handleChangeFavorite} reset={handleReset} />
                </div>
                <div className='col-lg-4 col-md-12 col-sm-12 col-xs-12 summary-wrapper'>
                    <MaterialCartSummary
                        cart={cart}
                        reset={handleReset}
                        setVoucherError={setVoucherError}
                        showBlinder={showBlinder}
                        onPaypalExpressError={setPaypalExpressError}
                    />
                </div>
                {!cart.promo?.applied && <Favorites className='w-100 col-12 no-material-margin' />}
            </div>
            {!isMobile && user.activeReferralPromotion && <RecommendedPromoMaterials />}
        </>
    );

    return (
        <>
            <div className='cart-page'>
                <CartHeaderMobile cartItems={cartItems} className='d-block d-lg-none' />
                <div
                    className={classNames('container', {
                        empty: cart.loaded && !cartItems.length
                    })}
                >
                    {Boolean(!cart.loaded || blinder) && <Blinder />}
                    {cart.loaded && (
                        <>
                            {promoError && <ErrorMessage onClose={onClosePromoError} error={{ message: promoError }} />}
                            {voucherError && (
                                <ErrorMessage onClose={onCloseVoucherError} error={{ message: voucherError.text }} />
                            )}
                            {paypalExpressError && (
                                <ErrorMessage onClose={onClosePaypalExpressError} error={paypalExpressError} />
                            )}
                            {Boolean(itemsInBundles && itemsInBundles.length) && (
                                <div className='alert alert-warning duplicate-warning text-center'>
                                    <span>{t('cart:cart.messages.bundleWarning')}</span>
                                    <span className='remove-duplicates-btn' onClick={removeDuplicated}>
                                        {t('cart:cart.messages.removeDuplicatedBundles')}
                                    </span>
                                </div>
                            )}
                            {cartItems.length ? CartContent : <NoResults />}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

const translate = translation(['cart', 'referrals-landing']);

export default translate(Cart);
